/**
 * FormFeedback Component
 *
 * Provides visual feedback for form submissions and validation.
 * Supports success, error, warning, and info states.
 *
 * @example
 * <FormFeedback type="success" message="Changes saved successfully!" />
 * <FormFeedback type="error" message="Failed to save" details="Please try again" />
 */

import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const FEEDBACK_TYPES = {
  success: {
    icon: CheckCircle,
    bgColor: 'bg-success-500/10',
    borderColor: 'border-success-500/30',
    textColor: 'text-success-400',
    iconColor: 'text-success-400',
  },
  error: {
    icon: XCircle,
    bgColor: 'bg-error-500/10',
    borderColor: 'border-error-500/30',
    textColor: 'text-error-400',
    iconColor: 'text-error-400',
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-warning-500/10',
    borderColor: 'border-warning-500/30',
    textColor: 'text-warning-400',
    iconColor: 'text-warning-400',
  },
  info: {
    icon: Info,
    bgColor: 'bg-secondary-500/10',
    borderColor: 'border-secondary-500/30',
    textColor: 'text-secondary-400',
    iconColor: 'text-secondary-400',
  },
};

/**
 * Inline form feedback message
 */
const FormFeedback = ({
  type = 'info',
  message,
  details,
  onDismiss,
  dismissible = true,
  className = '',
}) => {
  if (!message) return null;

  const config = FEEDBACK_TYPES[type] || FEEDBACK_TYPES.info;
  const Icon = config.icon;

  return (
    <div
      className={`
        flex items-start gap-3 p-3 rounded-lg border
        ${config.bgColor} ${config.borderColor} ${className}
      `}
      role="alert"
      aria-live="polite"
    >
      <Icon
        className={`h-5 w-5 flex-shrink-0 mt-0.5 ${config.iconColor}`}
        aria-hidden="true"
      />

      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${config.textColor}`}>{message}</p>
        {details && (
          <p className="text-sm text-gray-400 mt-1">{details}</p>
        )}
      </div>

      {dismissible && onDismiss && (
        <button
          onClick={onDismiss}
          className="flex-shrink-0 p-1 rounded hover:bg-white/10 transition-colors"
          aria-label="Dismiss message"
        >
          <X className="h-4 w-4 text-gray-400" />
        </button>
      )}
    </div>
  );
};

/**
 * Field-level validation message
 */
export const FieldError = ({ message, className = '' }) => {
  if (!message) return null;

  return (
    <p
      className={`text-xs text-error-400 mt-1 flex items-center gap-1 ${className}`}
      role="alert"
    >
      <XCircle className="h-3 w-3" aria-hidden="true" />
      {message}
    </p>
  );
};

/**
 * Field-level success message
 */
export const FieldSuccess = ({ message, className = '' }) => {
  if (!message) return null;

  return (
    <p className={`text-xs text-success-400 mt-1 flex items-center gap-1 ${className}`}>
      <CheckCircle className="h-3 w-3" aria-hidden="true" />
      {message}
    </p>
  );
};

/**
 * Form submission status indicator
 */
export const SubmissionStatus = ({
  status, // 'idle' | 'submitting' | 'success' | 'error'
  messages = {},
  onRetry,
  className = '',
}) => {
  const defaultMessages = {
    submitting: 'Saving...',
    success: 'Saved successfully!',
    error: 'Failed to save. Please try again.',
    ...messages,
  };

  if (status === 'idle') return null;

  if (status === 'submitting') {
    return (
      <div
        className={`flex items-center gap-2 text-sm text-gray-400 ${className}`}
        role="status"
        aria-live="polite"
      >
        <svg
          className="animate-spin h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        {defaultMessages.submitting}
      </div>
    );
  }

  if (status === 'success') {
    return (
      <FormFeedback
        type="success"
        message={defaultMessages.success}
        dismissible={false}
        className={className}
      />
    );
  }

  if (status === 'error') {
    return (
      <div className={className}>
        <FormFeedback
          type="error"
          message={defaultMessages.error}
          dismissible={false}
        />
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-2 text-sm text-error-400 hover:text-error-300 underline"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  return null;
};

/**
 * Hook for managing form feedback state
 */
export const useFormFeedback = (autoHideDuration = 5000) => {
  const [feedback, setFeedback] = React.useState(null);
  const timeoutRef = React.useRef(null);

  const showFeedback = React.useCallback((type, message, details) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setFeedback({ type, message, details });

    if (autoHideDuration > 0 && type === 'success') {
      timeoutRef.current = setTimeout(() => {
        setFeedback(null);
      }, autoHideDuration);
    }
  }, [autoHideDuration]);

  const clearFeedback = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setFeedback(null);
  }, []);

  const showSuccess = React.useCallback((message, details) => {
    showFeedback('success', message, details);
  }, [showFeedback]);

  const showError = React.useCallback((message, details) => {
    showFeedback('error', message, details);
  }, [showFeedback]);

  const showWarning = React.useCallback((message, details) => {
    showFeedback('warning', message, details);
  }, [showFeedback]);

  const showInfo = React.useCallback((message, details) => {
    showFeedback('info', message, details);
  }, [showFeedback]);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    feedback,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    clearFeedback,
    FeedbackComponent: feedback ? (
      <FormFeedback
        type={feedback.type}
        message={feedback.message}
        details={feedback.details}
        onDismiss={clearFeedback}
      />
    ) : null,
  };
};

/**
 * Validation helpers
 */
export const validators = {
  required: (value) => (!value || (typeof value === 'string' && !value.trim()) ? 'This field is required' : null),
  email: (value) => {
    if (!value) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !emailRegex.test(value) ? 'Please enter a valid email address' : null;
  },
  minLength: (min) => (value) => {
    if (!value) return null;
    return value.length < min ? `Must be at least ${min} characters` : null;
  },
  maxLength: (max) => (value) => {
    if (!value) return null;
    return value.length > max ? `Must be no more than ${max} characters` : null;
  },
  pattern: (regex, message) => (value) => {
    if (!value) return null;
    return !regex.test(value) ? message : null;
  },
  url: (value) => {
    if (!value) return null;
    try {
      new URL(value);
      return null;
    } catch {
      return 'Please enter a valid URL';
    }
  },
};

export default FormFeedback;
